import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import pb from '../lib/pocketbase';
import { RecordModel } from 'pocketbase';

interface Subscription {
  id: string;
  plan: 'free' | 'pro' | 'enterprise';
  status: 'active' | 'canceled' | 'past_due' | 'trialing';
  current_period_end: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
}

interface AuthContextType {
  user: RecordModel | null;
  subscription: Subscription | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Check for admin bypass in localStorage
const getAdminBypass = (): { user: any; subscription: Subscription } | null => {
  try {
    const savedUser = localStorage.getItem('ltss_user');
    const savedSub = localStorage.getItem('ltss_subscription');
    if (savedUser && savedSub) {
      const user = JSON.parse(savedUser);
      if (user.email === 'admin@vhss.org') {
        const sub = JSON.parse(savedSub);
        return { 
          user, 
          subscription: {
            id: 'admin',
            plan: sub.plan || 'enterprise',
            status: sub.status || 'active',
            current_period_end: '',
          }
        };
      }
    }
  } catch (e) {}
  return null;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const adminBypass = getAdminBypass();
  const [user, setUser] = useState<RecordModel | null>(
    adminBypass?.user || (pb.authStore.model as RecordModel | null)
  );
  const [subscription, setSubscription] = useState<Subscription | null>(
    adminBypass?.subscription || null
  );
  const [isLoading, setIsLoading] = useState(!adminBypass);
  const [isAdminMode, setIsAdminMode] = useState(!!adminBypass);

  // Fetch user's subscription
  const refreshSubscription = useCallback(async () => {
    // Don't override admin bypass subscription
    if (isAdminMode) {
      return;
    }
    
    if (!pb.authStore.isValid || !pb.authStore.model) {
      setSubscription(null);
      return;
    }

    try {
      const records = await pb.collection('subscriptions').getList(1, 1, {
        filter: `user = "${pb.authStore.model.id}"`,
        sort: '-created',
      });

      if (records.items.length > 0) {
        const sub = records.items[0];
        setSubscription({
          id: sub.id,
          plan: sub.plan,
          status: sub.status,
          current_period_end: sub.current_period_end,
          stripe_customer_id: sub.stripe_customer_id,
          stripe_subscription_id: sub.stripe_subscription_id,
        });
      } else {
        // No subscription = free tier
        setSubscription({
          id: '',
          plan: 'free',
          status: 'active',
          current_period_end: '',
        });
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      setSubscription({
        id: '',
        plan: 'free',
        status: 'active',
        current_period_end: '',
      });
    }
  }, [isAdminMode]);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = pb.authStore.onChange((token, model) => {
      setUser(model as RecordModel | null);
      if (model) {
        refreshSubscription();
      } else {
        setSubscription(null);
      }
    });

    // Initial load
    if (pb.authStore.isValid) {
      setUser(pb.authStore.model as RecordModel);
      refreshSubscription().finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }

    return () => {
      unsubscribe();
    };
  }, [refreshSubscription]);

  const login = async (email: string, password: string) => {
    const authData = await pb.collection('ltss_users').authWithPassword(email, password);
    setUser(authData.record);
    await refreshSubscription();
  };

  const register = async (email: string, password: string, name: string) => {
    // Create user
    await pb.collection('ltss_users').create({
      email,
      password,
      passwordConfirm: password,
      name,
    });

    // Auto-login after registration
    await login(email, password);
  };

  const logout = () => {
    pb.authStore.clear();
    localStorage.removeItem('ltss_user');
    localStorage.removeItem('ltss_subscription');
    localStorage.removeItem('ltss_admin');
    localStorage.removeItem('ltss_superadmin');
    setUser(null);
    setSubscription(null);
    setIsAdminMode(false);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        subscription,
        isLoading,
        isAuthenticated: isAdminMode || pb.authStore.isValid,
        login,
        register,
        logout,
        refreshSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
